'use client'

import { motion } from 'framer-motion'
import { BackgroundBeams } from '../../../_components/_ui/background-beams'
import Link from 'next/link'

export default function InfrastructurePage() {
    return (
        <div className="min-h-screen w-full relative">
            <BackgroundBeams />
            <div className="relative z-10 container mx-auto px-4 py-16">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="max-w-5xl mx-auto"
                >
                    <div className="mb-8">
                        <Link href="/support/documentation" className="text-indigo-400 hover:text-indigo-300 text-sm">
                            ← Back to Documentation
                        </Link>
                    </div>

                    <h1 className="text-4xl font-bold text-white mb-6">
                        Infrastructure & Deployment
                    </h1>
                    
                    <div className="prose prose-lg prose-invert max-w-none">
                        <p className="text-gray-300 text-lg mb-8">
                            Autofocus runs on a modern cloud infrastructure combining AWS Amplify for hosting, 
                            GitHub Actions for CI/CD, and Supabase for backend services. This setup ensures 
                            scalability, reliability, and fast global content delivery.
                        </p>

                        {/* Deployment Overview */}
                        <div className="bg-neutral-900/50 backdrop-blur-sm rounded-lg p-6 mb-8 border border-neutral-800">
                            <h2 className="text-2xl font-bold text-white mb-4">
                                🚀 Deployment Pipeline
                            </h2>
                            <div className="space-y-6">
                                <div>
                                    <h3 className="text-lg font-semibold text-green-400 mb-3">Git-to-Production Workflow</h3>
                                    <div className="bg-neutral-800 p-4 rounded">
                                        <div className="font-mono text-sm text-gray-300 space-y-2">
                                            <div><span className="text-blue-400">1.</span> Developer pushes to <code>main</code> branch</div>
                                            <div><span className="text-green-400">2.</span> GitHub Actions triggers build process</div>
                                            <div><span className="text-purple-400">3.</span> AWS Amplify detects repository changes</div>
                                            <div><span className="text-orange-400">4.</span> Next.js application builds with optimization</div>
                                            <div><span className="text-cyan-400">5.</span> Static assets deployed to global CDN</div>
                                            <div><span className="text-yellow-400">6.</span> Live site updated automatically</div>
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="grid md:grid-cols-2 gap-4">
                                    <div>
                                        <h4 className="font-semibold text-blue-400 mb-2">Deployment Triggers</h4>
                                        <ul className="text-gray-300 text-sm space-y-1">
                                            <li>• Push to main branch</li>
                                            <li>• Pull request merges</li>
                                            <li>• Manual deployment triggers</li>
                                            <li>• Scheduled deployments (if configured)</li>
                                        </ul>
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-purple-400 mb-2">Build Optimization</h4>
                                        <ul className="text-gray-300 text-sm space-y-1">
                                            <li>• Automatic code splitting</li>
                                            <li>• Image optimization and WebP conversion</li>
                                            <li>• Bundle size analysis</li>
                                            <li>• Tree shaking for unused code</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* AWS Amplify Configuration */}
                        <div className="bg-neutral-900/50 backdrop-blur-sm rounded-lg p-6 mb-8 border border-neutral-800">
                            <h2 className="text-2xl font-bold text-white mb-4">
                                ☁️ AWS Amplify Configuration
                            </h2>
                            <div className="space-y-6">
                                <div>
                                    <h3 className="text-lg font-semibold text-orange-400 mb-3">Build Configuration</h3>
                                    <p className="text-gray-300 text-sm mb-4">
                                        Amplify uses the <code>amplify.yml</code> file to define build steps, 
                                        artifact locations, and caching strategies.
                                    </p>
                                    <pre className="bg-neutral-800 p-4 rounded text-sm text-gray-300 overflow-x-auto">
{`# amplify.yml
version: 1
frontend:
  phases:
    preBuild:
      commands:
        - npm ci                    # Install dependencies
    build:
      commands:
        - npm run build           # Next.js production build
  artifacts:
    baseDirectory: .next         # Build output directory
    files:
      - '**/*'                    # Include all build artifacts
  cache:
    paths:
      - node_modules/**/*        # Cache dependencies
      - .next/cache/**/*         # Cache Next.js build cache`}
                                    </pre>
                                </div>
                                
                                <div>
                                    <h3 className="text-lg font-semibold text-cyan-400 mb-3">Environment Configuration</h3>
                                    <div className="grid md:grid-cols-2 gap-4">
                                        <div>
                                            <h4 className="font-semibold text-green-400 mb-2">Environment Variables</h4>
                                            <div className="bg-neutral-800 p-3 rounded text-xs">
                                                <div className="text-gray-400 mb-2"># Supabase Configuration</div>
                                                <div className="text-gray-300 space-y-1">
                                                    <div>NEXT_PUBLIC_SUPABASE_URL</div>
                                                    <div>NEXT_PUBLIC_SUPABASE_ANON_KEY</div>
                                                    <div>SUPABASE_SERVICE_ROLE_KEY</div>
                                                </div>
                                                <div className="text-gray-400 mb-2 mt-3"># Application Settings</div>
                                                <div className="text-gray-300 space-y-1">
                                                    <div>NEXT_PUBLIC_APP_URL</div>
                                                    <div>NODE_ENV</div>
                                                </div>
                                            </div>
                                        </div>
                                        <div>
                                            <h4 className="font-semibold text-blue-400 mb-2">Build Settings</h4>
                                            <ul className="text-gray-300 text-sm space-y-1">
                                                <li>• Node.js 18.x runtime</li>
                                                <li>• npm for package management</li>
                                                <li>• Next.js 14 framework detection</li>
                                                <li>• Automatic HTTPS certificate</li>
                                                <li>• Global CDN distribution</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                                
                                <div>
                                    <h3 className="text-lg font-semibold text-yellow-400 mb-3">Custom Domain & SSL</h3>
                                    <div className="grid md:grid-cols-2 gap-4">
                                        <div>
                                            <h4 className="font-semibold text-purple-400 mb-2">Domain Configuration</h4>
                                            <div className="bg-neutral-800 p-3 rounded text-sm">
                                                <div className="text-gray-300 space-y-1">
                                                    <div><span className="text-blue-400">Primary:</span> autofoc.us</div>
                                                    <div><span className="text-green-400">Redirect:</span> www.autofoc.us → autofoc.us</div>
                                                    <div><span className="text-orange-400">SSL:</span> Automatic certificate management</div>
                                                    <div><span className="text-purple-400">DNS:</span> Route 53 integration</div>
                                                </div>
                                            </div>
                                        </div>
                                        <div>
                                            <h4 className="font-semibold text-red-400 mb-2">Performance Features</h4>
                                            <ul className="text-gray-300 text-sm space-y-1">
                                                <li>• HTTP/2 and HTTP/3 support</li>
                                                <li>• Gzip and Brotli compression</li>
                                                <li>• Edge caching for static assets</li>
                                                <li>• Global content delivery network</li>
                                                <li>• Automatic security headers</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* GitHub Actions */}
                        <div className="bg-neutral-900/50 backdrop-blur-sm rounded-lg p-6 mb-8 border border-neutral-800">
                            <h2 className="text-2xl font-bold text-white mb-4">
                                🔄 GitHub Actions CI/CD
                            </h2>
                            <div className="space-y-6">
                                <div>
                                    <h3 className="text-lg font-semibold text-indigo-400 mb-3">Automated Workflows</h3>
                                    <p className="text-gray-300 text-sm mb-4">
                                        GitHub Actions handle continuous integration, testing, and deployment triggers 
                                        to ensure code quality and seamless deployments.
                                    </p>
                                    
                                    <div className="space-y-4">
                                        <div>
                                            <h4 className="font-semibold text-green-400 mb-2">Build & Test Workflow</h4>
                                            <pre className="bg-neutral-800 p-3 rounded text-xs text-gray-300 overflow-x-auto">
{`# .github/workflows/ci.yml
name: CI/CD Pipeline
on:
  push:
    branches: [main, develop]
  pull_request:
    branches: [main]

jobs:
  test:
    runs-on: ubuntu-latest
    steps:
      - uses: actions/checkout@v3
      - uses: actions/setup-node@v3
        with:
          node-version: '18'
          cache: 'npm'
      
      - name: Install dependencies
        run: npm ci
      
      - name: Run linter
        run: npm run lint
      
      - name: Run type check
        run: npm run type-check
      
      - name: Build application
        run: npm run build
        env:
          NEXT_PUBLIC_SUPABASE_URL: \${{ secrets.NEXT_PUBLIC_SUPABASE_URL }}
          NEXT_PUBLIC_SUPABASE_ANON_KEY: \${{ secrets.NEXT_PUBLIC_SUPABASE_ANON_KEY }}
      
      - name: Run E2E tests
        run: npm run cypress:run`}
                                            </pre>
                                        </div>
                                        
                                        <div>
                                            <h4 className="font-semibold text-orange-400 mb-2">Deployment Trigger</h4>
                                            <pre className="bg-neutral-800 p-3 rounded text-xs text-gray-300 overflow-x-auto">
{`  deploy:
    needs: test
    runs-on: ubuntu-latest
    if: github.ref == 'refs/heads/main'
    steps:
      - name: Trigger Amplify Deployment
        run: |
          curl -X POST \\
            -H "Authorization: token \${{ secrets.AMPLIFY_WEBHOOK_TOKEN }}" \\
            "\${{ secrets.AMPLIFY_WEBHOOK_URL }}"
      
      - name: Wait for deployment
        run: |
          echo "Deployment triggered. Check AWS Amplify console for status."
      
      - name: Notify Slack
        if: always()
        uses: 8398a7/action-slack@v3
        with:
          status: \${{ job.status }}
          webhook_url: \${{ secrets.SLACK_WEBHOOK }}`}
                                            </pre>
                                        </div>
                                    </div>
                                </div>
                                
                                <div>
                                    <h3 className="text-lg font-semibold text-purple-400 mb-3">Quality Gates</h3>
                                    <div className="grid md:grid-cols-2 gap-4">
                                        <div>
                                            <h4 className="font-semibold text-cyan-400 mb-2">Pre-Deployment Checks</h4>
                                            <ul className="text-gray-300 text-sm space-y-1">
                                                <li>• ESLint code quality checks</li>
                                                <li>• TypeScript type validation</li>
                                                <li>• Unit test suite execution</li>
                                                <li>• Build success verification</li>
                                                <li>• Bundle size analysis</li>
                                            </ul>
                                        </div>
                                        <div>
                                            <h4 className="font-semibold text-yellow-400 mb-2">Post-Deployment Validation</h4>
                                            <ul className="text-gray-300 text-sm space-y-1">
                                                <li>• End-to-end test execution</li>
                                                <li>• Lighthouse performance audit</li>
                                                <li>• Accessibility compliance checks</li>
                                                <li>• SSL certificate validation</li>
                                                <li>• Health check endpoint monitoring</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Performance & Monitoring */}
                        <div className="bg-neutral-900/50 backdrop-blur-sm rounded-lg p-6 mb-8 border border-neutral-800">
                            <h2 className="text-2xl font-bold text-white mb-4">
                                📊 Performance & Monitoring
                            </h2>
                            <div className="space-y-6">
                                <div>
                                    <h3 className="text-lg font-semibold text-blue-400 mb-3">Performance Optimization</h3>
                                    <div className="grid md:grid-cols-2 gap-4">
                                        <div>
                                            <h4 className="font-semibold text-green-400 mb-2">Next.js Optimizations</h4>
                                            <ul className="text-gray-300 text-sm space-y-1">
                                                <li>• Image optimization with WebP/AVIF</li>
                                                <li>• Automatic code splitting</li>
                                                <li>• Static generation where possible</li>
                                                <li>• Dynamic imports for heavy components</li>
                                                <li>• Bundle analyzer integration</li>
                                            </ul>
                                        </div>
                                        <div>
                                            <h4 className="font-semibold text-purple-400 mb-2">CDN & Caching</h4>
                                            <ul className="text-gray-300 text-sm space-y-1">
                                                <li>• Global edge locations</li>
                                                <li>• Static asset caching (1 year)</li>
                                                <li>• HTML caching with invalidation</li>
                                                <li>• API response caching</li>
                                                <li>• Browser cache optimization</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                                
                                <div>
                                    <h3 className="text-lg font-semibold text-orange-400 mb-3">Monitoring & Analytics</h3>
                                    <div className="grid md:grid-cols-2 gap-4">
                                        <div>
                                            <h4 className="font-semibold text-cyan-400 mb-2">AWS CloudWatch</h4>
                                            <div className="bg-neutral-800 p-3 rounded text-sm">
                                                <div className="text-gray-300 space-y-1">
                                                    <div>• Build time and success rate</div>
                                                    <div>• Deployment frequency</div>
                                                    <div>• Error rate monitoring</div>
                                                    <div>• Performance metrics</div>
                                                    <div>• Custom log analysis</div>
                                                </div>
                                            </div>
                                        </div>
                                        <div>
                                            <h4 className="font-semibold text-red-400 mb-2">Application Monitoring</h4>
                                            <div className="bg-neutral-800 p-3 rounded text-sm">
                                                <div className="text-gray-300 space-y-1">
                                                    <div>• Real user monitoring (RUM)</div>
                                                    <div>• Core Web Vitals tracking</div>
                                                    <div>• JavaScript error reporting</div>
                                                    <div>• API response time monitoring</div>
                                                    <div>• User journey analytics</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                
                                <div>
                                    <h3 className="text-lg font-semibold text-yellow-400 mb-3">Next.js Configuration</h3>
                                    <pre className="bg-neutral-800 p-4 rounded text-sm text-gray-300 overflow-x-auto">
{`// next.config.js - Production optimizations
const nextConfig = {
  productionBrowserSourceMaps: true,    // Enable source maps for debugging
  reactStrictMode: true,                // Strict mode for development
  output: 'standalone',                 // Optimized for serverless deployment
  
  webpack: (config, { isServer }) => {
    config.experiments = { 
      ...config.experiments, 
      topLevelAwait: true                // Enable top-level await
    }
    
    // Client-side optimizations
    if (!isServer) {
      config.resolve.fallback = {
        fs: false,
        path: false,
        os: false,
      }
    }
    
    return config
  },
  
  images: {
    unoptimized: true,                  // Handled by Amplify
    remotePatterns: [
      {
        protocol: 'https',
        hostname: 'i.imgur.com',
        pathname: '/**',
      },
      {
        protocol: 'https',
        hostname: 'axldgpriuvgigpfwqxrg.supabase.co',
        pathname: '/**',
      }
    ]
  }
}`}
                                    </pre>
                                </div>
                            </div>
                        </div>

                        {/* Scaling Considerations */}
                        <div className="bg-neutral-900/50 backdrop-blur-sm rounded-lg p-6 mb-8 border border-neutral-800">
                            <h2 className="text-2xl font-bold text-white mb-4">
                                📈 Scaling & High Availability
                            </h2>
                            <div className="space-y-6">
                                <div>
                                    <h3 className="text-lg font-semibold text-green-400 mb-3">Horizontal Scaling</h3>
                                    <div className="grid md:grid-cols-2 gap-4">
                                        <div>
                                            <h4 className="font-semibold text-blue-400 mb-2">Frontend Scaling</h4>
                                            <ul className="text-gray-300 text-sm space-y-1">
                                                <li>• Automatic global distribution via CDN</li>
                                                <li>• Edge computing for dynamic content</li>
                                                <li>• Serverless function scaling</li>
                                                <li>• Regional failover capabilities</li>
                                                <li>• Traffic-based auto-scaling</li>
                                            </ul>
                                        </div>
                                        <div>
                                            <h4 className="font-semibold text-purple-400 mb-2">Backend Scaling</h4>
                                            <ul className="text-gray-300 text-sm space-y-1">
                                                <li>• Supabase connection pooling</li>
                                                <li>• Read replica distribution</li>
                                                <li>• Database query optimization</li>
                                                <li>• API rate limiting and throttling</li>
                                                <li>• Caching layer optimization</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                                
                                <div>
                                    <h3 className="text-lg font-semibold text-orange-400 mb-3">Disaster Recovery</h3>
                                    <div className="bg-neutral-800 p-4 rounded">
                                        <h4 className="font-semibold text-red-400 mb-2">Backup Strategy</h4>
                                        <div className="text-gray-300 text-sm space-y-2">
                                            <div><strong>Database:</strong> Automatic daily backups with point-in-time recovery (Supabase)</div>
                                            <div><strong>Code:</strong> Git repository with multiple remote backups</div>
                                            <div><strong>Assets:</strong> S3 bucket replication across regions</div>
                                            <div><strong>Configuration:</strong> Infrastructure as Code with version control</div>
                                            <div><strong>Recovery Time:</strong> Target RTO &lt; 1 hour, RPO &lt; 15 minutes</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Security Considerations */}
                        <div className="bg-neutral-900/50 backdrop-blur-sm rounded-lg p-6 mb-8 border border-neutral-800">
                            <h2 className="text-2xl font-bold text-white mb-4">
                                🔒 Security & Compliance
                            </h2>
                            <div className="grid md:grid-cols-2 gap-6">
                                <div>
                                    <h3 className="text-lg font-semibold text-red-400 mb-3">Infrastructure Security</h3>
                                    <ul className="text-gray-300 text-sm space-y-2">
                                        <li>• <strong>HTTPS Only:</strong> TLS 1.3 with automatic certificate renewal</li>
                                        <li>• <strong>Security Headers:</strong> HSTS, CSP, X-Frame-Options</li>
                                        <li>• <strong>Network Security:</strong> WAF protection and DDoS mitigation</li>
                                        <li>• <strong>Access Control:</strong> IAM roles with least privilege</li>
                                        <li>• <strong>Vulnerability Scanning:</strong> Automated dependency checks</li>
                                    </ul>
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-blue-400 mb-3">Data Protection</h3>
                                    <ul className="text-gray-300 text-sm space-y-2">
                                        <li>• <strong>Encryption at Rest:</strong> AES-256 for all stored data</li>
                                        <li>• <strong>Encryption in Transit:</strong> TLS 1.3 for all connections</li>
                                        <li>• <strong>Secret Management:</strong> AWS Secrets Manager integration</li>
                                        <li>• <strong>Data Isolation:</strong> Row-level security policies</li>
                                        <li>• <strong>Audit Logging:</strong> Comprehensive access logs</li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        {/* Next Steps */}
                        <div className="bg-indigo-500/10 border border-indigo-500/20 rounded-lg p-6">
                            <h3 className="text-lg font-semibold text-white mb-4">
                                🚀 Explore More
                            </h3>
                            <div className="grid md:grid-cols-2 gap-4">
                                <Link href="/support/documentation/advanced" className="block p-4 rounded-lg bg-neutral-800/50 hover:bg-neutral-800 transition-colors">
                                    <div className="font-semibold text-yellow-400 mb-1">Advanced Features →</div>
                                    <div className="text-gray-300 text-sm">Analytics, admin tools, and platform extensibility</div>
                                </Link>
                                <Link href="/support/documentation/architecture" className="block p-4 rounded-lg bg-neutral-800/50 hover:bg-neutral-800 transition-colors">
                                    <div className="font-semibold text-blue-400 mb-1">Architecture Overview →</div>
                                    <div className="text-gray-300 text-sm">High-level system architecture and tech stack</div>
                                </Link>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </div>
        </div>
    )
}